import { takeEvery, call, put, select } from 'redux-saga/effects';
import {
  fetchAllOrganizations,
  fetchOrganizationUsers,
  updateOrganization,
  fetchHightouchAllotmentSyncLimit,
  updateHightouchAllotmentSyncLimit,
} from 'api/AllOrganizationsAPI';
import { getErrorResponse } from 'utils/xhr';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { addToRootSaga } from 'store';
import { selectOrganizationById } from './selectors';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { OrgParamProps, OrganizationUser, HightouchAllotment } from './types';

/**
 * fetchAllOrganizationsSaga
 * Saga for fetching all organizations
 * @return { void }
 */
export function* fetchAllOrganiationsSaga() {
  try {
    const response: OrgParamProps[] = yield call(fetchAllOrganizations);
    yield put(actions.fetchAllOrganizationsResponse(response));
  } catch (error) {
    const err = getErrorResponse(error);
    const message = `Error fetching all organizations. ${err}`;
    yield put(addNegativeToast(message));
  }
}

/**
 * fetchOrganizationUsersSaga
 * Saga for fetching users of an organization given its id
 * @return { void }
 */
export function* fetchOrganizationUsersSaga({
  orgId,
}: ReturnType<typeof actions.fetchOrganizationUsersRequest>) {
  try {
    const response: OrganizationUser[] = yield call(fetchOrganizationUsers, orgId);
    yield put(actions.fetchOrganizationUsersResponse(response, orgId));
  } catch (error) {
    const err = getErrorResponse(error);
    const message = `Error fetching organization users. ${err}`;
    yield put(addNegativeToast(message));
  }
}

/**
 * updateOrganization
 * Saga for updating organization details
 * @return { void }
 */
export function* updateOrganizationSaga({
  orgId,
  params,
}: ReturnType<typeof actions.updateOrganizationRequest>) {
  try {
    /**
     * The backend expects the entire organization object as part of the API request,
     * however, the UI only allows for us to update a small part of that object. So,
     * we fetch the existing organization details and override its props with the updated
     * values from the form, before sending the entire object to the backend.
     */
    const existingOrgDetails: OrgParamProps = yield select((state) =>
      selectOrganizationById(state, orgId),
    );
    const updatedOrganization = { ...existingOrgDetails, ...params };
    yield call(updateOrganization, orgId, updatedOrganization);
    yield put(addPositiveToast('Organization updated successfully'));
    yield put(actions.fetchAllOrganizationsRequest());
  } catch (error) {
    const err = getErrorResponse(error);
    const message = `Error updating organization. ${err}`;
    yield put(addNegativeToast(message));
  }
}

/**
 * fetchHightouchAllotmentSyncLimitSaga
 * Saga for fetching the hightouch allotment for the organization
 */
export function* fetchHightouchAllotmentSyncLimitSaga() {
  try {
    const response: HightouchAllotment = yield call(fetchHightouchAllotmentSyncLimit);
    yield put(actions.fetchHightouchAllotmentResponse(response));
  } catch (error) {
    const err = getErrorResponse(error);
    const message = `Error fetching hightouch allotment sync limit. ${err}`;
    yield put(addNegativeToast(message));
  }
}

/**
 * updateHightouchAllotmentSyncLimitSaga
 * Saga for updating the hightouch allotment for the organization
 */
export function* updateHightouchAllotmentSyncLimitSaga({
  syncs,
}: ReturnType<typeof actions.updateHightouchAllotmentRequest>) {
  try {
    const response: string = yield call(updateHightouchAllotmentSyncLimit, syncs);
    yield put(addPositiveToast(response));
  } catch (error) {
    const err = getErrorResponse(error);
    const message = `Error updating hightouch allotment sync limit. ${err}`;
    yield put(addNegativeToast(message));
  }
}

addToRootSaga([
  takeEvery(actionTypes.FETCH_ALL_ORGANIZATIONS_REQUEST, fetchAllOrganiationsSaga),
  takeEvery(actionTypes.FETCH_ORGANIZATION_USERS_REQUEST, fetchOrganizationUsersSaga),
  takeEvery(actionTypes.UPDATE_ORGANIZATION_REQUEST, updateOrganizationSaga),
  takeEvery(
    actionTypes.FETCH_HIGHTOUCH_ALLOTMENT_SYNC_LIMIT_REQUEST,
    fetchHightouchAllotmentSyncLimitSaga,
  ),
  takeEvery(
    actionTypes.UPDATE_HIGHTOUCH_ALLOTMENT_SYNC_LIMIT_REQUEST,
    updateHightouchAllotmentSyncLimitSaga,
  ),
]);
